import React, { useEffect, useMemo, useState } from "react";
import "../../App.css";
import { apiPostAuth } from "../../utils/api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

export default function MainPage() {
  const [loading, setLoading] = useState(true);

  const [lifetimeLoanAmount, setLifetimeLoanAmount] = useState(0);
  const [lifetimePaidInterest, setLifetimePaidInterest] = useState(0);
  const [activeLoanAmount, setActiveLoanAmount] = useState(0);
  const [activeUnpaidInterest, setActiveUnpaidInterest] = useState(0);
  const [activeLoanCount, setActiveLoanCount] = useState(0);

  const [monthlyDataList, setMonthlyDataList] = useState([]);

  useEffect(() => {
    fetchDashboardInit();
  }, []);

  const fetchDashboardInit = async () => {
    try {
      const response = await apiPostAuth("init.php");

      if (response.result === 1) {
        setLifetimeLoanAmount(
          response.lifetime_loan_amount || 0
        );

        setLifetimePaidInterest(
          response.lifetime_paid_interest || 0
        );

        setActiveLoanAmount(
          response.active_loan_amount || 0
        );

        setActiveUnpaidInterest(
          response.active_unpaid_interest || 0
        );

        setActiveLoanCount(
          response.active_loan_count + " Pinjaman Aktif" || 0
        );

        setMonthlyDataList(
          response.monthly_data_list || []
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString(
      "id-ID",
      {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }
    );
  };

  const formattedChartData = useMemo(() => {
    return monthlyDataList.map((item) => {
      const year = item.month_time_stamp.slice(
        0,
        4
      );

      const month = item.month_time_stamp.slice(
        4,
        6
      );

      const date = new Date(
        `${year}-${month}-01`
      );

      const monthLabel =
        date.toLocaleString("id-ID", {
          month: "short",
        });

      return {
        month: `${monthLabel} ${year}`,
        loan_amount: Number(
          item.loan_amount || 0
        ),
        interest_amount: Number(
          item.interest_amount || 0
        ),
      };
    });
  }, [monthlyDataList]);

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          marginTop: "50px",
        }}
      >
        <div className="loading-spinner"></div>
        <p>Memuat...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <section style={heroSectionStyle}>
        <div>
          <h1 style={pageTitleStyle}>
            Keuangan MoriDapin
          </h1>

          <p style={pageSubtitleStyle}>
            Pantau pertumbuhan Pinjaman dan pendapatan Bunga.
          </p>
        </div>

        <div style={heroBadgeStyle}>
          Dashboard
        </div>
      </section>

      <section style={chartSectionStyle}>
        <div style={chartHeaderStyle}>
          <div>
            <h2 style={sectionTitleStyle}>
              Pertumbuhan Bulanan
            </h2>

            <p style={sectionSubtitleStyle}>
              Nilai Pinjamanan dan Pendapatan Bunga per Bulan
            </p>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            height: "420px",
          }}
        >
          <ResponsiveContainer>
            <AreaChart
              data={formattedChartData}
              margin={{
                top: 20,
                right: 20,
                left: 0,
                bottom: 0,
              }}
            >
              <defs>
                <linearGradient
                  id="loanGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#2563eb"
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="95%"
                    stopColor="#2563eb"
                    stopOpacity={0}
                  />
                </linearGradient>

                <linearGradient
                  id="interestGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#10b981"
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="95%"
                    stopColor="#10b981"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f1f5f9"
              />

              <XAxis
                dataKey="month"
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
              />

              <YAxis
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
                tickFormatter={(value) =>
                  `Rp${(
                    value / 1000000
                  ).toFixed(0)} Jt`
                }
              />

              <Tooltip
                formatter={(value) =>
                  formatCurrency(value)
                }
                contentStyle={{
                  borderRadius: "12px",
                  border:
                    "1px solid #e2e8f0",
                  boxShadow:
                    "0 8px 24px rgba(0,0,0,0.08)",
                }}
              />

              <Legend />

              <Area
                type="monotone"
                dataKey="loan_amount"
                stroke="#2563eb"
                fill="url(#loanGradient)"
                strokeWidth={3}
                name="Nilai Pinjaman Pokok"
              />

              <Area
                type="monotone"
                dataKey="interest_amount"
                stroke="#10b981"
                fill="url(#interestGradient)"
                strokeWidth={3}
                name="Bunga Terbayarkan"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section style={metricGridStyle}>
        <MetricCard
          title="Total Pinjaman Pokok"
          value={formatCurrency(
            lifetimeLoanAmount
          )}
          subtitle="Pinjaman Selama Ini"
          accent="#2563eb"
          icon="💰"
        />

        <MetricCard
          title="Total Pendapatan Bunga"
          value={formatCurrency(
            lifetimePaidInterest
          )}
          subtitle="Bunga Selama Ini"
          accent="#10b981"
          icon="💵"
        />

        <MetricCard
          title="Total Pinjaman Aktif"
          value={formatCurrency(
            activeLoanAmount
          )}
          subtitle={activeLoanCount}
          accent="#f59e0b"
          icon="🏦"
        />

        <MetricCard
          title="Bunga Terhutang"
          value={formatCurrency(
            activeUnpaidInterest
          )}
          subtitle="Bunga Yang Belum Dibayar"
          accent="#ef4444"
          icon="⚠️"
        />
      </section>
    </div>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  accent,
  icon,
}) {
  return (
    <div
      style={{
        ...metricCardStyle,
        borderTop: `4px solid ${accent}`,
      }}
    >
      <div style={metricTopRowStyle}>
        <div
          style={{
            ...metricIconStyle,
            backgroundColor: `${accent}15`,
          }}
        >
          {icon}
        </div>

        <div
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: accent,
            opacity: 0.8,
          }}
        />
      </div>

      <p style={metricTitleStyle}>
        {title}
      </p>

      <h2 style={metricValueStyle}>
        {value}
      </h2>

      <p style={metricSubtitleStyle}>
        {subtitle}
      </p>
    </div>
  );
}

/* Styles */

const heroSectionStyle = {
  background:
    "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
  borderRadius: "20px",
  padding: "28px",
  color: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
  gap: "20px",
  flexWrap: "wrap",
  boxShadow:
    "0 10px 30px rgba(37,99,235,0.25)",
};

const pageTitleStyle = {
  margin: 0,
  fontSize: "2rem",
  fontWeight: "800",
};

const pageSubtitleStyle = {
  margin: "8px 0 0 0",
  opacity: 0.9,
  fontSize: "1rem",
};

const heroBadgeStyle = {
  backgroundColor: "rgba(255,255,255,0.15)",
  padding: "12px 18px",
  borderRadius: "999px",
  fontWeight: "700",
  backdropFilter: "blur(8px)",
};

const chartSectionStyle = {
  backgroundColor: "#fff",
  borderRadius: "20px",
  padding: "24px",
  marginBottom: "24px",
  boxShadow:
    "0 6px 18px rgba(0,0,0,0.05)",
};

const chartHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "18px",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.35rem",
  color: "#0f172a",
};

const sectionSubtitleStyle = {
  margin: "6px 0 0 0",
  color: "#64748b",
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "18px",
};

const metricCardStyle = {
  backgroundColor: "#fff",
  borderRadius: "18px",
  padding: "22px",
  boxShadow:
    "0 6px 18px rgba(0,0,0,0.05)",
  transition: "transform 0.2s ease",
};

const metricTopRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "18px",
};

const metricIconStyle = {
  width: "54px",
  height: "54px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
};

const metricTitleStyle = {
  margin: 0,
  color: "#64748b",
  fontSize: "0.95rem",
};

const metricValueStyle = {
  margin: "10px 0 8px 0",
  color: "#0f172a",
  fontSize: "1.65rem",
  fontWeight: "800",
  lineHeight: 1.2,
};

const metricSubtitleStyle = {
  margin: 0,
  color: "#94a3b8",
  fontSize: "0.88rem",
};